<template>
  <v-container fluid>
    <v-row class="mb-2" align="center">
      <v-col><h2 class="mb-0">Metrics</h2></v-col>
      <v-col cols="auto" class="d-flex align-center">
        <span v-if="lastUpdated" class="text-caption text--secondary mr-3">
          Updated {{ lastUpdatedFromNow }}
        </span>
        <v-select
          v-model="autoRefreshInterval"
          :items="refreshIntervals"
          label="Auto-refresh"
          dense
          hide-details
          outlined
          style="max-width: 160px"
          class="mr-2"
        />
        <v-btn icon @click="fetchMetrics" :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- KPI tiles -->
    <v-row dense>
      <v-col
        v-for="kpi in kpis"
        :key="kpi.label"
        cols="12"
        sm="6"
        md="3"
        lg="2"
      >
        <v-card outlined class="pa-4">
          <div class="text-caption text--secondary">{{ kpi.label }}</div>
          <div class="text-h5 font-weight-bold">{{ kpi.value }}</div>
          <div v-if="kpi.hint" class="text-caption text--secondary">
            {{ kpi.hint }}
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Top paths chart -->
    <v-row>
      <v-col cols="12" md="6">
        <v-card outlined>
          <v-card-title>Top paths by requests</v-card-title>
          <v-card-text>
            <apexchart
              type="bar"
              height="360"
              :options="topRequestsChart.options"
              :series="topRequestsChart.series"
            />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card outlined>
          <v-card-title>Top paths by errors</v-card-title>
          <v-card-text>
            <apexchart
              type="bar"
              height="360"
              :options="topErrorsChart.options"
              :series="topErrorsChart.series"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Detailed table -->
    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <v-card-title>
            All paths
            <v-spacer />
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
              dense
            />
          </v-card-title>

          <v-card-text class="pb-0">
            <div class="d-flex flex-wrap" style="gap: 8px">
              <v-chip
                v-for="f in statusFilters"
                :key="f.value"
                :color="statusFilter === f.value ? f.color : ''"
                :outlined="statusFilter !== f.value"
                :dark="statusFilter === f.value && f.color !== ''"
                small
                label
                @click="statusFilter = f.value"
              >
                {{ f.label }}
                <span class="ml-1 text--secondary">({{ f.count }})</span>
              </v-chip>

              <v-divider vertical class="mx-2" />

              <v-chip
                v-for="m in methodFilters"
                :key="m.value"
                :color="methodFilter === m.value ? methodColor(m.value) : ''"
                :outlined="methodFilter !== m.value"
                :dark="methodFilter === m.value"
                small
                label
                @click="methodFilter = methodFilter === m.value ? null : m.value"
              >
                {{ m.label }}
                <span class="ml-1" :class="methodFilter === m.value ? '' : 'text--secondary'">({{ m.count }})</span>
              </v-chip>

              <v-spacer />

              <v-chip
                v-if="statusFilter !== 'all' || methodFilter"
                small
                label
                color="grey lighten-3"
                @click="clearFilters"
              >
                <v-icon x-small left>mdi-close</v-icon>
                Clear filters
              </v-chip>
            </div>
          </v-card-text>

          <v-data-table
            :headers="headers"
            :items="filteredPaths"
            :search="search"
            :loading="loading"
            :items-per-page="20"
            sort-by="request_count"
            sort-desc
            dense
          >
            <template v-slot:item.method="{ item }">
              <v-chip small label :color="methodColor(item.method)" dark>
                {{ item.method }}
              </v-chip>
            </template>
            <template v-slot:item.error_count="{ item }">
              <span
                :class="
                  item.error_count > 0 ? 'error--text font-weight-bold' : ''
                "
              >
                {{ item.error_count }}
              </span>
            </template>
            <template v-slot:item.avg_ms="{ item }">
              {{ avg(item).toFixed(2) }}
            </template>
            <template v-slot:item.last_seen_at="{ item }">
              <span :title="item.last_seen_at">{{
                fromNow(item.last_seen_at)
              }}</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import moment from "moment";
import api from "@/services/api";

export default {
  name: "Metrics",
  components: { apexchart: VueApexCharts },
  data: () => ({
    loading: false,
    search: "",
    data: { user_count: 0, path_count: 0, paths: [], users: [] },
    lastUpdated: null,
    now: Date.now(),
    nowTimer: null,
    autoRefreshInterval: 0,
    autoRefreshTimer: null,
    refreshIntervals: [
      { text: "Off", value: 0 },
      { text: "10 s", value: 10 },
      { text: "30 s", value: 30 },
      { text: "1 min", value: 60 },
      { text: "5 min", value: 300 },
    ],
    statusFilter: "all",
    methodFilter: null,
    headers: [
      { text: "Method", value: "method", width: 90 },
      { text: "Path", value: "path" },
      { text: "Requests", value: "request_count", align: "end" },
      { text: "Errors", value: "error_count", align: "end" },
      { text: "Min ms", value: "duration_min_ms", align: "end" },
      { text: "Avg ms", value: "avg_ms", align: "end", sortable: false },
      { text: "Max ms", value: "duration_max_ms", align: "end" },
      { text: "Last seen", value: "last_seen_at" },
    ],
  }),
  computed: {
    paths() {
      return this.data.paths || [];
    },
    filteredPaths() {
      return this.paths.filter((p) => {
        if (this.statusFilter === "errors" && p.error_count === 0) return false;
        if (this.statusFilter === "clean" && p.error_count > 0) return false;
        if (this.methodFilter && p.method !== this.methodFilter) return false;
        return true;
      });
    },
    statusFilters() {
      const errs = this.paths.filter((p) => p.error_count > 0).length;
      const clean = this.paths.length - errs;
      return [
        { value: "all", label: "All", color: "primary", count: this.paths.length },
        { value: "errors", label: "With errors", color: "error", count: errs },
        { value: "clean", label: "No errors", color: "success", count: clean },
      ];
    },
    methodFilters() {
      const counts = this.paths.reduce((acc, p) => {
        acc[p.method] = (acc[p.method] || 0) + 1;
        return acc;
      }, {});
      return Object.keys(counts)
        .sort()
        .map((m) => ({ value: m, label: m, count: counts[m] }));
    },
    totalRequests() {
      return this.paths.reduce((s, p) => s + p.request_count, 0);
    },
    totalErrors() {
      return this.paths.reduce((s, p) => s + p.error_count, 0);
    },
    totalDuration() {
      return this.paths.reduce((s, p) => s + p.duration_sum_ms, 0);
    },
    lastUpdatedFromNow() {
      if (!this.lastUpdated) return "";
      const diff = Math.max(0, Math.floor((this.now - this.lastUpdated) / 1000));
      if (diff < 5) return "just now";
      if (diff < 60) return diff + "s ago";
      if (diff < 3600) return Math.floor(diff / 60) + "m ago";
      return Math.floor(diff / 3600) + "h ago";
    },
    kpis() {
      const errRate = this.totalRequests
        ? ((this.totalErrors / this.totalRequests) * 100).toFixed(1) + " %"
        : "0 %";
      const avg = this.totalRequests
        ? (this.totalDuration / this.totalRequests).toFixed(2) + " ms"
        : "0 ms";
      return [
        { label: "Requests", value: this.totalRequests },
        {
          label: "Errors",
          value: this.totalErrors,
          hint: errRate + " error rate",
        },
        { label: "Unique paths", value: this.data.path_count },
        { label: "Users", value: this.data.user_count },
        { label: "Avg latency", value: avg },
      ];
    },
    topRequestsChart() {
      return this.buildTopChart("request_count", "#1976d2");
    },
    topErrorsChart() {
      return this.buildTopChart("error_count", "#e53935");
    },
  },
  methods: {
    async fetchMetrics() {
      this.loading = true;
      try {
        const { data } = await api.get("admin/metrics/requests");
        this.data = data;
        this.lastUpdated = Date.now();
      } finally {
        this.loading = false;
      }
    },
    clearFilters() {
      this.statusFilter = "all";
      this.methodFilter = null;
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      if (this.autoRefreshInterval > 0) {
        this.autoRefreshTimer = setInterval(
          this.fetchMetrics,
          this.autoRefreshInterval * 1000
        );
      }
    },
    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }
    },
    avg(p) {
      return p.request_count ? p.duration_sum_ms / p.request_count : 0;
    },
    fromNow(iso) {
      return iso ? moment(iso).fromNow() : "-";
    },
    methodColor(m) {
      return (
        {
          GET: "blue",
          POST: "green",
          PUT: "orange",
          PATCH: "amber",
          DELETE: "red",
        }[m] || "grey"
      );
    },
    buildTopChart(field, color) {
      const top = [...this.filteredPaths]
        .sort((a, b) => b[field] - a[field])
        .slice(0, 10);
      return {
        series: [{ name: field, data: top.map((p) => p[field]) }],
        options: {
          chart: { toolbar: { show: false } },
          colors: [color],
          plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
          dataLabels: { enabled: false },
          xaxis: { categories: top.map((p) => `${p.method} ${p.path}`) },
        },
      };
    },
  },
  watch: {
    autoRefreshInterval() {
      this.startAutoRefresh();
    },
  },
  created() {
    this.fetchMetrics();
  },
  mounted() {
    this.nowTimer = setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    this.stopAutoRefresh();
    if (this.nowTimer) clearInterval(this.nowTimer);
  },
};
</script>
